import { ThunkDispatch } from 'redux-thunk';
import RootState from 'store/state';
import { AuditLogsResponse } from 'views/AdminSettings/AuditLogs/types'
import { CallbackQueue } from 'views/Settings/Queues/types';
import * as AgentAPI from 'services/api/api.agent';
import * as QueueAPI from 'services/api/api.queue';
import * as AuthAPI from 'services/api/api.auth';
import * as CompanyAPI from 'services/api/api.company';
import * as LinesAPI from 'services/api/api.line';
import * as ReportsAPI from 'services/api/api.report';
import * as AuditLogAPI from 'services/api/api.auditLog';
import * as CallbackQueuesAPI from 'services/api/api.callbackQueues';
import * as EmailAPI from 'services/api/api.email';
import * as WebformAPI from 'services/api/api.webform';

import SettingsTypes from './settings.types';
import {
    IGlobalLine,
    Line,
    IReport,
    SmartAgentUser,
    IGroup,
    IUserHierarchy,
    IHierarchyGroup,
    IAgentCreate,
    IQueue,
    PatchConnect,
    IPermission,
    AuthProviderConfig,
    IAppFeature,
    IContactFlow,
} from './settings.state';
import { IAgent } from '@missionlabs/smartagent-lib-shared/build/agent';
import { ISettings } from '@missionlabs/smartagent-lib-shared/build/settings';
import { SADropdownOption } from '@missionlabs/smartagent-app-components/dist/Dropdown';
import { batch } from 'react-redux';
import { AxiosError } from 'axios';
import { logout } from 'store/user/user.actions';
import { createNotification, NotificationAction } from 'store/notification/notification.actions';
import { isAnEmail } from './settings.utils';
import { AuditLogsQueryParams } from 'views/AdminSettings/AuditLogs/types';
import { UpdateCallbackQueueBody } from 'views/Settings/Queues/types';
import { IEmailTemplate } from 'store/channelSettings/email/email.types';
import { AppFeatures, IFeature } from 'store/app/app.features';
import { canUserUseFeature } from 'utils';

export type SettingsAction =
    | { type: typeof SettingsTypes.GET_LINES; payload: Array<Line> }
    | { type: typeof SettingsTypes.GET_LINES_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GET_GLOBAL_LINE; payload: IGlobalLine }
    | { type: typeof SettingsTypes.GET_GLOBAL_LINE_ERROR; }
    | { type: typeof SettingsTypes.UPDATE_GLOBAL_LINE; payload: IGlobalLine }
    | { type: typeof SettingsTypes.GET_GLOBAL_LINE_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.UPDATE_LINES; payload: Line }
    | { type: typeof SettingsTypes.UPDATE_LINES_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GET_REPORTS; payload: Array<IReport> }
    | { type: typeof SettingsTypes.GET_REPORTS_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GENERATE_REPORTS_LOADING; payload: { loading: boolean } }
    | {
        type: typeof SettingsTypes.SET_FILTERED_LINES; payload: {
            keyword: string,
            status: 'on' | 'off' | null
        }
    }
    | {
        type: typeof SettingsTypes.SET_RINGING_DEVICE;
        payload: { ringingDevice: SADropdownOption };
    }
    | { type: typeof SettingsTypes.GET_ROUTING_PROFILES; payload: Array<IGroup> }
    | { type: typeof SettingsTypes.GET_QUEUES; payload: Array<IQueue> | undefined }
    | { type: typeof SettingsTypes.GET_ENABLED_QUEUES; payload: Array<IQueue> | undefined }
    | { type: typeof SettingsTypes.GET_EMAIL_TEMPLATES; payload: Array<IEmailTemplate> }
    | { type: typeof SettingsTypes.GET_ALLOWED_ATTACHMENT_TYPES; payload: Record<string, string> }
    | { type: typeof SettingsTypes.GET_CONTACT_FLOWS; payload: Array<IContactFlow> }
    | { type: typeof SettingsTypes.GET_SECURITY_GROUPS; payload: Array<IGroup> }
    | { type: typeof SettingsTypes.GET_USER_HIERARCHY; payload: IUserHierarchy }
    | { type: typeof SettingsTypes.GET_HIERARCHY_GROUPS; payload: IHierarchyGroup[] }
    | { type: typeof SettingsTypes.GET_USERS; payload: Array<SmartAgentUser> }
    | { type: typeof SettingsTypes.GET_USERS_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GET_USERS_ERROR; payload: { error: boolean } }
    | { type: typeof SettingsTypes.DELETING_USER; payload: { agent: SmartAgentUser | null } }
    | { type: typeof SettingsTypes.DELETE_USER_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.DELETE_USER_ERROR; payload: { error: string } }
    | { type: typeof SettingsTypes.DELETE_USER; payload: SmartAgentUser }
    | { type: typeof SettingsTypes.DISABLING_USER; payload: { agent: SmartAgentUser | null } }
    | { type: typeof SettingsTypes.DISABLE_USER_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.DISABLE_USER_ERROR; payload: { error: string } }
    | { type: typeof SettingsTypes.DISABLE_USER; payload: SmartAgentUser }
    | { type: typeof SettingsTypes.ENABLING_USER; payload: { agent: SmartAgentUser | null } }
    | { type: typeof SettingsTypes.ENABLE_USER_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.ENABLE_USER_ERROR; payload: { error: string } }
    | { type: typeof SettingsTypes.ENABLE_USER; payload: SmartAgentUser }
    | { type: typeof SettingsTypes.ADD_USER; payload: SmartAgentUser }
    | { type: typeof SettingsTypes.ADDING_USER; payload: { adding: boolean } }
    | { type: typeof SettingsTypes.ADDING_USER_LOADING; payload: { adding: boolean } }
    | { type: typeof SettingsTypes.ADD_USER_ERROR; payload: { error: string } }
    | { type: typeof SettingsTypes.EDIT_USER_REFRESH; payload: { refresh: boolean } }
    | { type: typeof SettingsTypes.EDIT_USER_ERROR; payload: { error: string } }
    | { type: typeof SettingsTypes.EDITING_USER_LOADING; payload: { editing: boolean } }
    | { type: typeof SettingsTypes.EDITING_USER; payload: SmartAgentUser | null }
    | { type: typeof SettingsTypes.EDIT_USER; payload: SmartAgentUser }
    | { type: typeof SettingsTypes.GET_PERMISSIONS; payload: IPermission[] }
    | { type: typeof SettingsTypes.GET_PERMISSIONS_ERROR; payload: boolean }
    | { type: typeof SettingsTypes.GET_COGNITO_CONFIG; payload: AuthProviderConfig }
    | { type: typeof SettingsTypes.GET_SETTINGS; payload: ISettings }
    | { type: typeof SettingsTypes.GET_SETTINGS_LOADING; payload?: boolean }
    | { type: typeof SettingsTypes.EDIT_SETTINGS; payload: Partial<ISettings> }
    | { type: typeof SettingsTypes.EDIT_SETTINGS_LOADING; payload?: boolean; }
    | { type: typeof SettingsTypes.GET_FEATURE_LIST; payload: IAppFeature[] }
    | { type: typeof SettingsTypes.ENABLE_WEBHID; }
    | { type: typeof SettingsTypes.DISABLE_WEBHID; }

    | { type: typeof SettingsTypes.GET_AUDIT_LOGS_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GET_AUDIT_LOGS; payload: AuditLogsResponse }
    | { type: typeof SettingsTypes.GET_AUDIT_LOGS_ERROR; payload: { error: boolean } }

    | { type: typeof SettingsTypes.GET_SETTING_NAMES_FOR_AUDIT_LOGS_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GET_SETTING_NAMES_FOR_AUDIT_LOGS; payload: Array<string> }
    | { type: typeof SettingsTypes.GET_SETTING_NAMES_FOR_AUDIT_LOGS_ERROR; payload: { error: boolean } }

    | { type: typeof SettingsTypes.GET_USERS_LIST_FOR_AUDIT_LOGS_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GET_USERS_LIST_FOR_AUDIT_LOGS; payload: Array<string> }
    | { type: typeof SettingsTypes.GET_USERS_LIST_FOR_AUDIT_LOGS_ERROR; payload: { error: boolean } }

    | { type: typeof SettingsTypes.GET_CALLBACK_QUEUES_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.GET_CALLBACK_QUEUES; payload: CallbackQueue[] }
    | { type: typeof SettingsTypes.GET_CALLBACK_QUEUES_ERROR; payload: { error: boolean } }

    | { type: typeof SettingsTypes.UPDATE_CALLBACK_QUEUE_LOADING; payload: { loading: boolean } }
    | { type: typeof SettingsTypes.UPDATE_CALLBACK_QUEUE; payload: CallbackQueue }
    | { type: typeof SettingsTypes.UPDATE_CALLBACK_QUEUE_ERROR; payload: { error: boolean } }

export function setRingingDevice(device: SADropdownOption | null = null) {
    return {
        type: SettingsTypes.SET_RINGING_DEVICE,
        payload: device,
    };
}

export function setUserHasEnabledWebHID(): SettingsAction {
    return {
        type: SettingsTypes.ENABLE_WEBHID,
    };
}
export function setUserHasDisabledWebHID(): SettingsAction {
    return {
        type: SettingsTypes.DISABLE_WEBHID,
    };
}

// interface
export function patchGlobalLine(data: PatchConnect) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction | NotificationAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(getGlobalLineLoading(true));

        try {
            const response = await LinesAPI.updateConnectGlobalLine(companyID, token, data);

            dispatch({
                type: SettingsTypes.UPDATE_GLOBAL_LINE,
                payload: response,
            });

            dispatch(createNotification({
                header: 'Success!',
                text: 'All changes were successfully saved.',
                type: 'success',
            }));
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error updating lines');
        } finally {
            dispatch(getGlobalLineLoading(false));
        }
    };
}

export function getGlobalLine() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(getGlobalLineLoading(true));

        try {
            const response = await LinesAPI.getConnectGlobalLine(companyID, token);
            return dispatch({ type: SettingsTypes.GET_GLOBAL_LINE, payload: response });
        } catch (err) {
            console.log('Error getting the global line', err);
            dispatch(getGlobalLineError());

        } finally {
            dispatch(getGlobalLineLoading(false));
        }
    };
}

export function getGlobalLineError(): SettingsAction {
    return {
        type: SettingsTypes.GET_GLOBAL_LINE_ERROR,
    };
}

export function getLines() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(getLinesLoading(true));

        try {
            const response = await LinesAPI.getConnectLines(companyID, token);

            return dispatch({
                type: SettingsTypes.GET_LINES,
                payload: response,
            });
        } catch (err) {
            console.log('Error getting lines', err);
        } finally {
            dispatch(getLinesLoading(false));
        }
    };
}

export function addUserLoading(adding = true): SettingsAction {
    return {
        type: SettingsTypes.ADDING_USER_LOADING,
        payload: {
            adding,
        },
    };
}

export function editUserLoading(editing = true): SettingsAction {
    return {
        type: SettingsTypes.EDITING_USER_LOADING,
        payload: {
            editing,
        },
    };
}

export function getUsers() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;
        const reducedPayload = getState().app.appConfig?.reduceGetUserPayload;
        const fields = reducedPayload ? {
            fields: 'ID,username,firstName,lastName,email,routingProfileID,hierarchyGroupID,disabled'
        } : undefined;

        dispatch(getUsersLoading(true));

        try {
            const [bareUsers, routingProfiles, securityGroups, userHierarchy] = await Promise.all([
                AgentAPI.getUsers(companyID, instanceID, token, fields),

                AgentAPI.getRoutingProfiles(companyID, instanceID, token),

                AgentAPI.getSecurityGroups(companyID, instanceID, token),

                AgentAPI.getUserHierarchy(companyID, instanceID, token).catch(),
            ]);

            const users: Array<SmartAgentUser> = bareUsers.map((user: IAgent) => ({
                ...user,
                routingProfile: routingProfiles.find(
                    (rp: IGroup) => rp.Id === user.routingProfileID
                )?.Name,
            }));

            batch(() => {
                dispatch({
                    type: SettingsTypes.GET_ROUTING_PROFILES,
                    payload: routingProfiles,
                });

                dispatch({
                    type: SettingsTypes.GET_SECURITY_GROUPS,
                    payload: securityGroups,
                });

                dispatch({
                    type: SettingsTypes.GET_USER_HIERARCHY,
                    payload: userHierarchy ?? {},
                });

                dispatch({
                    type: SettingsTypes.GET_USERS,
                    payload: users,
                });
            });
        } catch (err) {
            console.log('err :', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(
                getUsersError(true)
            )
        }
    };
}

export function getUsersError(error: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_USERS_ERROR,
        payload: { error },
    };
}

export function addUser(agent: IAgentCreate) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, identityManagement } = getState().app;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;
        const { routingProfiles, cognitoConfig } = getState().settings;
        if (identityManagement && identityManagement !== 'connect') {
            delete agent.password;
        }
        if (agent.hierarchyGroupID === '') {
            delete agent.hierarchyGroupID;
        }

        switch (identityManagement) {
            case 'cognito':
                // If email is a username attribute and the user set their username to an email,
                // then we set the agents email to be their username. This avoids the user having to input an email twice
                if (!cognitoConfig?.usernameAttributes?.find(u => u === 'email') &&
                    isAnEmail(agent.username) && !agent.email)
                    agent.email = agent.username;
                break;
        }

        dispatch(addUserLoading());

        try {
            let newUser = await AgentAPI.postUser(ID, instanceID, token, agent);

            console.log('created user ', newUser);

            newUser = {
                ...newUser,
                routingProfile: routingProfiles.find(
                    (rp: IGroup) => rp.Id === agent.routingProfileID
                )!.Name,
            } as SmartAgentUser;

            dispatch({
                type: SettingsTypes.ADD_USER,
                payload: newUser as SmartAgentUser,
            });
        } catch (err) {
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(addUserError(e.response?.data.error));
        }
    };
}

export function setAddingUser(adding: boolean = true): SettingsAction {
    return {
        type: SettingsTypes.ADDING_USER,
        payload: {
            adding,
        },
    };
}

export function addUserError(error: string): SettingsAction {
    return {
        type: SettingsTypes.ADD_USER_ERROR,
        payload: {
            error,
        },
    };
}

export function editRefresh(refresh: boolean = true): SettingsAction {
    return {
        type: SettingsTypes.EDIT_USER_REFRESH,
        payload: {
            refresh,
        },
    };
}

// For the function which actually makes the put req to edit the user, see editUser()
// This is just for loading in an agent to edit
export function editingUser(agent?: SmartAgentUser | null) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        if (!agent) {
            return dispatch({
                type: SettingsTypes.EDITING_USER,
                payload: null,
            });
        }

        const companyID = getState().app.ID;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;

        try {
            dispatch(editRefresh());

            const response: SmartAgentUser = await AgentAPI.getSingleUser(
                companyID,
                instanceID,
                token,
                agent.ID
            );

            dispatch({
                type: SettingsTypes.EDITING_USER,
                payload: response,
            });
        } catch (err) {
            console.log('error refreshing user', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            if (e?.response?.status === 404) {
                dispatch(editUserError('User not found'));
            } else {
                dispatch(editUserError('Something went wrong'));
            }
        }
    };
}

export function editUser(agent: Partial<IAgentCreate>) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, any>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;
        const routingProfiles = getState().settings.routingProfiles;

        try {
            dispatch(editUserLoading());

            let changedUser = await AgentAPI.putUser(companyID, instanceID, token, agent);

            console.log('AgentAPI putUser success', changedUser);

            changedUser = {
                ...changedUser,
                routingProfile: routingProfiles.find(
                    (rp: IGroup) => rp.Id === agent.routingProfileID
                )!.Name,
            } as SmartAgentUser;

            dispatch({
                type: SettingsTypes.EDIT_USER,
                payload: changedUser,
            });
        } catch (err) {
            console.error('error putting user', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(editUserError(e.response?.data.error));
        }
    };
}

export function editUserError(error: string): SettingsAction {
    return {
        type: SettingsTypes.EDIT_USER_ERROR,
        payload: {
            error,
        },
    };
}

export function deletingUser(agent: SmartAgentUser | null = null): SettingsAction {
    return {
        type: SettingsTypes.DELETING_USER,
        payload: {
            agent,
        },
    };
}

export function disablingUser(agent: SmartAgentUser | null = null): SettingsAction {
    return {
        type: SettingsTypes.DISABLING_USER,
        payload: {
            agent,
        },
    };
}
export function disableUserLoading(loading: boolean = true): SettingsAction {
    return {
        type: SettingsTypes.DISABLE_USER_LOADING,
        payload: {
            loading,
        },
    };
}
export function disableUserError(error: string): SettingsAction {
    return {
        type: SettingsTypes.DISABLE_USER_ERROR,
        payload: {
            error,
        },
    };
}
export function enablingUser(agent: SmartAgentUser | null = null): SettingsAction {
    return {
        type: SettingsTypes.ENABLING_USER,
        payload: {
            agent,
        },
    };
}
export function enableUserLoading(loading: boolean = true): SettingsAction {
    return {
        type: SettingsTypes.ENABLE_USER_LOADING,
        payload: {
            loading,
        },
    };
}
export function enableUserError(error: string): SettingsAction {
    return {
        type: SettingsTypes.ENABLE_USER_ERROR,
        payload: {
            error,
        },
    };
}

export function deleteUserLoading(loading: boolean = true): SettingsAction {
    return {
        type: SettingsTypes.DELETE_USER_LOADING,
        payload: {
            loading,
        },
    };
}

export function deleteUserError(error: string): SettingsAction {
    return {
        type: SettingsTypes.DELETE_USER_ERROR,
        payload: {
            error,
        },
    };
}

export function deleteUser(agent: SmartAgentUser) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;

        dispatch(deleteUserLoading());

        try {
            await AgentAPI.deleteUser(companyID, instanceID, agent.ID, token);

            console.log(`deleted agent`, agent);

            dispatch({
                type: SettingsTypes.DELETE_USER,
                payload: agent,
            });
        } catch (err) {
            console.log("couldn't delete agent", err, agent);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(deleteUserError(e.response?.data.error));
        }
    };
}

export function enableUser(agent: SmartAgentUser) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;

        try {
            dispatch(enableUserLoading(true));

            await AgentAPI.putUser(companyID, instanceID, token, {
                ID: agent.ID,
                disabled: false
            });

            dispatch({
                type: SettingsTypes.ENABLE_USER,
                payload: agent,
            });

            dispatch(enableUserLoading(false));
        } catch (err) {
            console.log('error enabling user', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(enableUserError(e.response?.data.error));
        }
    }
}

export function disableUser(agent: SmartAgentUser) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;

        try {
            dispatch(disableUserLoading(true));

            await AgentAPI.putUser(companyID, instanceID, token, {
                ID: agent.ID,
                disabled: true
            });

            dispatch({
                type: SettingsTypes.DISABLE_USER,
                payload: agent,
            });

            dispatch(disableUserLoading(false));
        } catch (err) {
            console.log('error disabling user', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout())

            dispatch(disableUserError(e.response?.data.error));
        }
    }
}

export function getUsersLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_USERS_LOADING,
        payload: {
            loading,
        },
    };
}

export function getReportsLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_REPORTS_LOADING,
        payload: {
            loading,
        },
    };
}

export function getReports() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(getReportsLoading(true));

        try {
            const response = await ReportsAPI.getReports(companyID, token);

            dispatch({
                type: SettingsTypes.GET_REPORTS,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'Unable to get reports')
        } finally {
            dispatch(getReportsLoading(false));
        }
    };
}

export function generateReport(
    reportID: string,
    startDate: string,
    endDate: string,
    email: string
) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(generateReportsLoading(true));

        try {
            const response = await ReportsAPI.generateReport(
                companyID,
                token,
                reportID,
                startDate,
                endDate,
                email
            );

            return response.data;
        } catch (err) {
            return settingsAPIError(err, dispatch, 'Error generating report')
        } finally {
            dispatch(generateReportsLoading(false));
        }
    };
}

export function generateReportsLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GENERATE_REPORTS_LOADING,
        payload: {
            loading,
        },
    };
}

export function getLinesLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_LINES_LOADING,
        payload: {
            loading,
        },
    };
}

export function getGlobalLineLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_GLOBAL_LINE_LOADING,
        payload: {
            loading,
        },
    };
}

export function updateLinesLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.UPDATE_LINES_LOADING,
        payload: {
            loading,
        },
    };
}

export function patchLines(lineID: string, data: PatchConnect) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction | NotificationAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(updateLinesLoading(true));

        try {
            const response = await LinesAPI.updateConnectLines(companyID, token, lineID, data);

            dispatch({
                type: SettingsTypes.UPDATE_LINES,
                payload: response,
            });

            dispatch(createNotification({
                header: 'Success!',
                text: 'All changes were successfully saved.',
                type: 'success',
            }));
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error updating lines')
        } finally {
            dispatch(updateLinesLoading(false));
        }
    };
}

export function getHierarchyGroups() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await AgentAPI.getHierarchyGroups(ID, instance!.ID, token);

            dispatch({
                type: SettingsTypes.GET_HIERARCHY_GROUPS,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting hierarchy groups')
        }
    };
}

export function getRoutingProfiles() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await AgentAPI.getRoutingProfiles(ID, instance!.ID, token);

            dispatch({
                type: SettingsTypes.GET_ROUTING_PROFILES,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting routing profiles')
        }
    };
}

export function getSecurityGroups() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await AgentAPI.getSecurityGroups(ID, instance!.ID, token);

            dispatch({
                type: SettingsTypes.GET_SECURITY_GROUPS,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting routing profiles');
        }
    };
}

export function getPermissions() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await AuthAPI.getPermissions(ID, token);

            const { rolePermissions } = response;

            if (!Array.isArray(rolePermissions)) {
                return dispatch({
                    type: SettingsTypes.GET_PERMISSIONS_ERROR,
                    payload: true,
                });
            }

            dispatch({
                type: SettingsTypes.GET_PERMISSIONS,
                payload: rolePermissions,
            });
        } catch (err) {
            dispatch({
                type: SettingsTypes.GET_PERMISSIONS_ERROR,
                payload: true,
            });

            return settingsAPIError(err, dispatch, 'error getting routing profiles');
        }
    };
}

export function savePermissions(rolePermissions: IPermission[]) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await AuthAPI.putPermissions(ID, token, {
                ID,
                rolePermissions
            });

            dispatch({
                type: SettingsTypes.GET_PERMISSIONS,
                payload: response.rolePermissions,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting routing profiles');
        }
    };
}

export function getQueues() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await QueueAPI.getQueues(ID, instance!.ID, token);

            dispatch({
                type: SettingsTypes.GET_QUEUES,
                payload: response,
            });
        } catch (err) {
            console.error(`Error getting queues: ${JSON.stringify(err)}`);
        }
    };
}

export function getEnabledQueues(userTags: Record<string, string>) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await QueueAPI.getEnabledQueues(ID, instance!.ID, userTags, token);
            const mappedData = response.filter(queue => queue.name?.[0] !== '_').map(queue => ({
                Name: queue.name,
                Arn: queue.resourceId,
                Id: queue.id,
                QueueType: queue.queueType
            }))

            dispatch({
                type: SettingsTypes.GET_ENABLED_QUEUES,
                payload: mappedData,
            });
        } catch (err) {
            console.error(`Error getting queues: ${JSON.stringify(err)}`);
        }
    };
}

export function getRTMQueues(userTags: Record<string, string>) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { app, auth } = getState()
        const hasFeature = canUserUseFeature(app.features, auth.features ?? [])

        if (hasFeature(AppFeatures.REALTIME_DATA_QUEUES_REDESIGNED)) {
            return dispatch(getEnabledQueues(userTags))
        } else {
            return dispatch(getQueues())
        }
    };
}

export function getEmailTemplates() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
    ) => {
        try {
            const response = await EmailAPI.getEmailTemplates();

            dispatch({
                type: SettingsTypes.GET_EMAIL_TEMPLATES,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting emailTemplates')
        }
    };
}

export function getAllowedAttachmentTypes() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
    ) => {
        try {
            const response = await WebformAPI.getFileTypes();

            dispatch({
                type: SettingsTypes.GET_ALLOWED_ATTACHMENT_TYPES,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting allowedAttachmentTypes')
        }
    };
}

export function getContactFlows() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await QueueAPI.getContactFlows(ID, instance!.ID, token);

            dispatch({
                type: SettingsTypes.GET_CONTACT_FLOWS,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting contact flows')
        }
    };
}

export function getUserHierarchy() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;
        const token = getState().auth.token!;

        try {
            const response = await AgentAPI.getUserHierarchy(ID, instance!.ID, token);

            dispatch({
                type: SettingsTypes.GET_USER_HIERARCHY,
                payload: response,
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting user hierarchy')
        }
    };
}

export function getFeatureList() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
    ) => {
        try {
            const response = await AuthAPI.getFeatureList();

            dispatch({
                type: SettingsTypes.GET_FEATURE_LIST,
                payload: response
            });
        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting feature list')
        }
    };
}

export function getAuthProviderConfig() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { ID, instance } = getState().app;

        try {
            const response = await AuthAPI.getAuthProviderConfig(ID, instance!.ID);

            switch (response.identityManagement) {
                case 'cognito':
                    dispatch({
                        type: SettingsTypes.GET_COGNITO_CONFIG,
                        payload: response ?? {},
                    });
                    break;
            }

        } catch (err) {
            return settingsAPIError(err, dispatch, 'error getting auth provider config');
        }
    };
}

export function getSettings() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;

        dispatch(getSettingsLoading())

        try {
            const response = await CompanyAPI.getSettings(companyID, token);

            dispatch({
                type: SettingsTypes.GET_SETTINGS,
                payload: response
            });

        } catch (err) {

            return settingsAPIError(err, dispatch, 'error getting settings');

        }
    };
}

const getSettingsLoading = (loading = true): SettingsAction => ({
    type: SettingsTypes.GET_SETTINGS_LOADING,
    payload: loading
});

export function editSettings(settings: Partial<ISettings>) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction | NotificationAction>,
        getState: () => RootState
    ) => {
        dispatch(editSettingsLoading())

        try {
            const companyID = getState().app.ID;
            const token = getState().auth.token!;

            const response = await CompanyAPI.putSettings(companyID, token, settings);

            dispatch({
                type: SettingsTypes.EDIT_SETTINGS,
                payload: response.data
            })

            dispatch(createNotification({
                header: 'Success!',
                text: 'All changes were successfully saved.',
                type: 'success',
            }))

        } catch (error) {
            dispatch(editSettingsLoading(false))

            console.log(error);
        }
    }
}

const editSettingsLoading = (loading = true): SettingsAction => ({
    type: SettingsTypes.EDIT_SETTINGS_LOADING,
    payload: loading
})

const settingsAPIError = (err: any, dispatch: ThunkDispatch<RootState, void, SettingsAction>, msg?: string) => {
    console.error(msg, err);
    const e = err as AxiosError;

    if (e.response?.status === 403) {
        return dispatch(logout())
    }
}


export function getAuditLogsLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_AUDIT_LOGS_LOADING,
        payload: {
            loading,
        },
    };
}

export function getAuditLogs(auditLogsQueryParams: AuditLogsQueryParams) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { app, auth } = getState();
        const companyID = app.ID;
        const token = auth.token!;

        dispatch(getAuditLogsLoading(true));
        try {
            const auditLogs = await AuditLogAPI.getAuditLogs(companyID, token, auditLogsQueryParams);

            dispatch({
                type: SettingsTypes.GET_AUDIT_LOGS,
                payload: auditLogs,
            });
        } catch (err) {
            console.error('Error getting audit logs :', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout());

            dispatch(
                getAuditLogsError(true)
            );
        }
    };
}

export function getAuditLogsError(error: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_AUDIT_LOGS_ERROR,
        payload: { error },
    };
}

export function getSettingNamesForAuditLogsLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_SETTING_NAMES_FOR_AUDIT_LOGS_LOADING,
        payload: {
            loading,
        },
    };
}

export function getSettingNamesForAuditLogs() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { app, auth } = getState();
        const companyID = app.ID;
        const token = auth.token!;

        dispatch(getSettingNamesForAuditLogsLoading(true));
        try {
            const settingNames = await AuditLogAPI.getSettingNames(companyID, token);

            dispatch({
                type: SettingsTypes.GET_SETTING_NAMES_FOR_AUDIT_LOGS,
                payload: settingNames,
            });
        } catch (err) {
            console.error('Error getting setting names :', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout());

            dispatch(
                getSettingNamesForAuditLogsError(true)
            );
        }
    };
}

export function getSettingNamesForAuditLogsError(error: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_SETTING_NAMES_FOR_AUDIT_LOGS_ERROR,
        payload: { error },
    };
}

export function getUsersListForAuditLogsLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_USERS_LIST_FOR_AUDIT_LOGS_LOADING,
        payload: {
            loading,
        },
    };
}

export function getUsersListForAuditLogs() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState
    ) => {
        const { app, auth } = getState();
        const companyID = app.ID;
        const token = auth.token!;

        dispatch(getUsersListForAuditLogsLoading(true));
        try {
            const usersList = await AuditLogAPI.getUsersList(companyID, token);

            dispatch({
                type: SettingsTypes.GET_USERS_LIST_FOR_AUDIT_LOGS,
                payload: usersList,
            });
        } catch (err) {
            console.error('Error getting user list :', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout());

            dispatch(
                getUsersListForAuditLogsError(true)
            );
        }
    };
}

export function getUsersListForAuditLogsError(error: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_USERS_LIST_FOR_AUDIT_LOGS_ERROR,
        payload: { error },
    };
}

export function getCallbackQueuesLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_CALLBACK_QUEUES_LOADING,
        payload: {
            loading,
        },
    };
}

export function getCallbackQueues() {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction>,
        getState: () => RootState,
    ) => {
        const { auth, app } = getState();
        const companyID = app.ID;
        const instanceID = app.instance!.ID;
        const token = auth.token!;

        dispatch(getCallbackQueuesLoading(true));
        try {
            const callBackQueues = await CallbackQueuesAPI.getCallBackQueues(token, companyID, instanceID);

            dispatch({
                type: SettingsTypes.GET_CALLBACK_QUEUES,
                payload: callBackQueues,
            });
        } catch (err) {
            console.error('Error getting callBack queues :', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout());

            dispatch(
                getCallbackQueuesError(true)
            );
        }
    };
}

export function getCallbackQueuesError(error: boolean): SettingsAction {
    return {
        type: SettingsTypes.GET_CALLBACK_QUEUES_ERROR,
        payload: { error },
    };
}

export function updateCallbackQueueLoading(loading: boolean): SettingsAction {
    return {
        type: SettingsTypes.UPDATE_CALLBACK_QUEUE_LOADING,
        payload: {
            loading,
        },
    };
}

export function updateCallbackQueue(
    updateCallbackQueueBody: UpdateCallbackQueueBody,
    queueID: string,
) {
    return async (
        dispatch: ThunkDispatch<RootState, void, SettingsAction | NotificationAction>,
        getState: () => RootState,
    ) => {
        const { auth, app } = getState();
        const companyID = app.ID;
        const instanceID = app.instance!.ID;
        const token = auth.token!;

        dispatch(updateCallbackQueueLoading(true));
        try {
            const callBackQueue = await CallbackQueuesAPI.updateCallBackQueue(
                token,
                companyID,
                instanceID,
                queueID,
                updateCallbackQueueBody,
            );
            dispatch({
                type: SettingsTypes.UPDATE_CALLBACK_QUEUE,
                payload: callBackQueue,
            });

            dispatch(createNotification({
                type: 'success',
                header: 'Success',
                text: 'All your changes have been successfully saved',
            }));
        } catch (err) {
            console.error('Error getting callBack queues :', err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout());

            dispatch(
                updateCallbackQueueError(true)
            );

            dispatch(createNotification({
                type: 'error',
                header: 'Error',
                text: 'Oops something went wrong, please try again later',
            }));
        }
    };
}

export function updateCallbackQueueError(error: boolean): SettingsAction {
    return {
        type: SettingsTypes.UPDATE_CALLBACK_QUEUE_ERROR,
        payload: { error },
    };
}

